// /quote checkout page: render the localStorage cart into the page, let the
// user adjust quantities, and submit name/email/mobile + items to the server.

import { ServiceResponse, serviceCall, resolveUrl } from "@serenity-is/corelib";
import showNotification from "../../../Views/Shared/Notification/NotificationAlert";
import { trackSiteEvent } from "../_Shared/siteEvents";
import { attributionForServer } from "../_Shared/attribution";
import { quoteStore, thumbSrc, QuoteItem, langPrefix, productHref, escapeHtml, PLACEHOLDER } from "./quoteStore";
import { recaptchaToken } from "../../Common/Helpers/recaptcha";


// Localized UI strings injected server-side as window.feText (English fallbacks below).
const FT: Record<string, string> = (window as any).feText || {};
const tr = (key: string, fallback: string): string => FT[key] || fallback;
/** The same text HTML-escaped, for markup templates: translations are editable in Administration. */
const trh = (key: string, fallback: string): string => escapeHtml(tr(key, fallback));
const LP: string = langPrefix();

interface QuoteResponse extends ServiceResponse {
    success: boolean;
    message: string;
}

const ICON_TRASH =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>';

let listEl: HTMLElement;
let emptyEl: HTMLElement;
let formWrapEl: HTMLElement;
let countEl: HTMLElement | null;
let clearBtn: HTMLElement | null;
let submitBtn: HTMLButtonElement | null;
let form: HTMLFormElement | null;

document.addEventListener("DOMContentLoaded", () => {
    listEl = document.querySelector("[data-quote-list]") as HTMLElement;
    emptyEl = document.querySelector("[data-quote-empty]") as HTMLElement;
    formWrapEl = document.querySelector("[data-quote-form-wrap]") as HTMLElement;
    countEl = document.querySelector("[data-quote-count]");
    clearBtn = document.querySelector("[data-quote-clear]");
    submitBtn = document.querySelector("[data-quote-submit]") as HTMLButtonElement;
    form = document.getElementById("quoteForm") as HTMLFormElement;
    if (!listEl || !form) return;

    clearBtn?.addEventListener("click", () => {
        if (quoteStore.lineCount() > 0) quoteStore.clear();
    });

    form.addEventListener("submit", onSubmit);
    quoteStore.subscribe(render);
    render();
});

function render(): void {
    const items = quoteStore.getItems();
    const totalQty = quoteStore.count();

    if (countEl) countEl.textContent = totalQty ? `(${totalQty})` : "";

    const empty = items.length === 0;
    emptyEl.hidden = !empty;
    listEl.hidden = empty;
    formWrapEl.classList.toggle("is-disabled", empty);
    if (clearBtn) (clearBtn as HTMLElement).hidden = empty;
    if (submitBtn) submitBtn.disabled = empty;

    if (empty) {
        listEl.innerHTML = "";
        return;
    }

    listEl.innerHTML = items.map(rowHtml).join("");
    listEl.querySelectorAll<HTMLElement>("[data-row]").forEach(wireRow);
}

function rowHtml(item: QuoteItem): string {
    const href = escapeHtml(productHref(item, LP));
    return `
        <div class="qpage__row" data-row="${item.id}">
            <a class="qpage__row-img" href="${href}">
                <img src="${escapeHtml(thumbSrc(item.thumb))}" alt="${escapeHtml(item.name)}" loading="lazy"
                     onerror="this.onerror=null;this.src='${PLACEHOLDER}';">
            </a>
            <div class="qpage__row-main">
                <a class="qpage__row-name" href="${href}">${escapeHtml(item.name)}</a>
                <span class="qpage__row-id">${trh("Quote.Ref", "Ref #")}${item.id}</span>
            </div>
            <div class="qpage__row-actions">
                <div class="qcart-stepper" role="group" aria-label="${trh("Cart.Qty", "Quantity")}">
                    <button type="button" class="qcart-stepper__btn" data-dec aria-label="${trh("Cart.Dec", "Decrease quantity")}">−</button>
                    <input class="qcart-stepper__val" type="text" inputmode="numeric" value="${item.qty}" aria-label="${trh("Cart.Qty", "Quantity")}" />
                    <button type="button" class="qcart-stepper__btn" data-inc aria-label="${trh("Cart.Inc", "Increase quantity")}">+</button>
                </div>
                <button type="button" class="qpage__row-rm" data-rm aria-label="${trh("Cart.Remove", "Remove")} ${escapeHtml(item.name)}">${ICON_TRASH}</button>
            </div>
        </div>`;
}

function wireRow(row: HTMLElement): void {
    const id = Number(row.getAttribute("data-row"));
    const input = row.querySelector(".qcart-stepper__val") as HTMLInputElement;
    const qty = () => {
        const n = parseInt(input.value, 10);
        return isNaN(n) ? 1 : n;
    };
    row.querySelector("[data-dec]")?.addEventListener("click", () => quoteStore.setQty(id, qty() - 1));
    row.querySelector("[data-inc]")?.addEventListener("click", () => quoteStore.setQty(id, qty() + 1));
    input.addEventListener("change", () => quoteStore.setQty(id, qty()));
    row.querySelector("[data-rm]")?.addEventListener("click", () => {
        row.classList.add("is-leaving");
        window.setTimeout(() => quoteStore.remove(id), 180);
    });
}

async function onSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (!form) return;

    const items = quoteStore.getItems();
    if (items.length === 0) {
        showNotification(tr("Quote.EmptyMsg", "Your quote is empty. Please add at least one product."), "Error");
        return;
    }
    if (!form.checkValidity()) {
        form.reportValidity();
        return;
    }

    if (submitBtn) submitBtn.disabled = true;
    try {
        const fd = new FormData(form);
        const data = {
            Attribution: attributionForServer(),
            FullName: fd.get("FullName") as string,
            Phone: fd.get("Phone") as string,
            EmailAddress: fd.get("EmailAddress") as string,
            Message: (fd.get("Message") as string) ?? "",
            Website: (fd.get("Website") as string) ?? "",
            Subject: (fd.get("Subject") as string) ?? "",
            FormToken: (fd.get("FormToken") as string) ?? "",
            RecaptchaToken: await recaptchaToken(form, "quote"),
            Items: items.map((i) => ({ ProductId: i.id, Quantity: i.qty }))
        };
        await submit(data);
    } finally {
        if (submitBtn) submitBtn.disabled = quoteStore.lineCount() === 0;
    }
}

function submit(data: unknown): Promise<void> {
    // data-submit-url is rendered through Url.Loc and keeps the /ar prefix, so the
    // endpoint answers in the language the visitor is browsing. resolveUrl is the
    // fallback if the attribute is ever missing.
    const submitUrl = form?.getAttribute("data-submit-url");
    return new Promise<void>((resolve) => {
        serviceCall<QuoteResponse>({
            url: submitUrl || resolveUrl("~/quote/submit"),
            request: data,
            method: "POST",
            onSuccess: (response) => {
                if (response && response.success === false) {
                    showNotification(response.message || tr("Quote.GenericErr", "Something went wrong. Please try again."), "Error");
                    resolve();
                    return;
                }
                trackSiteEvent("quote_request");
                quoteStore.clear();
                form?.reset();
                showSuccess(response?.message || tr("Quote.Ok", "Quote request sent successfully!"));
                resolve();
            },
            onError: () => {
                showNotification(tr("Quote.SendErr", "Something went wrong while sending your request. Please try again later."), "Error");
                resolve();
            }
        });
    });
}

function showSuccess(msg: string): void {
    showNotification(msg, "Success");
    const grid = document.querySelector(".qpage__grid");
    if (grid) {
        grid.innerHTML = `
            <div class="qpage__done">
                <div class="qpage__done-ico">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                </div>
                <h2>${trh("Quote.Received", "Request received")}</h2>
                <p>${trh("Quote.ReceivedText", "Thank you — our team will review your products and reply with a tailored quotation, usually within three business days.")}</p>
                <a href="${LP}/products" class="v8-btn">${trh("Quote.Continue", "Continue browsing")}</a>
            </div>`;
    }
}

